import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { useRouter } from 'next/router';

import { hasChildren } from '@/utils';
import Image from 'next/image';

const SingleLevel = ({ item = {}, parentSlug }) => {
  const router = useRouter();

  const isSelectedChild = useMemo(
    () =>
      router?.query?.productId?.[Number(router?.query?.productId?.length) - 1] ===
      item?.attributes?.slug,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router?.query?.productId]
  );
  let fontWeight = 500;
  if (item?.attributes?.name) fontWeight = 500;
  if (isSelectedChild) fontWeight = 500;

  const handleClickItem = () => {
    router.push(
      `/document/${router?.query?.productId?.[0]}/${parentSlug ? `${parentSlug}/` : ''}${
        item?.attributes?.slug
      }`,
      undefined,
      { shallow: true }
    );
  };

  return (
    <ListItem
      disablePadding
      sx={{
        mb: 0,
        minHeight: 44,
        width: '100%',
        borderLeft: isSelectedChild ? '3px solid #14a8e4' : '3px solid #F2F2F2',
        borderRadius: '0 6px 6px 0',
        backgroundColor: isSelectedChild ? '#EAF9FE' : 'transparent',
        transition: 'background-color 160ms ease, border-color 160ms ease',
        '&:hover': { backgroundColor: isSelectedChild ? '#EAF9FE' : '#f0f9fc' },
      }}
    >
      {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
      <Typography
        onClick={handleClickItem}
        sx={{
          flex: 1,
          px: 1.5,
          py: 1,
          color: isSelectedChild ? '#14A8E4' : '#231F20',
          cursor: 'pointer',
          fontSize: 14,
          fontWeight,
          '&:hover': {
            color: '#087fba',
          },
        }}
      >
        {item?.attributes?.title || item?.attributes?.name}
      </Typography>
    </ListItem>
  );
};

const MultiLevel = ({ item = {} }) => {
  const children = item?.attributes?.document_pages?.data || [];
  const [open, setOpen] = useState(false);
  const router = useRouter();

  const isSelectedParent = useMemo(
    () =>
      router?.query?.productId &&
      Array.isArray(router?.query?.productId) &&
      router?.query?.productId?.includes(item?.attributes?.slug),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router?.query?.productId]
  );

  // get selected item for 2 layers
  const selectedItem = children.find((child) => router.asPath?.includes(child?.attributes?.slug));
  // set open for active menu
  useEffect(() => {
    if (!open && !!selectedItem) {
      setOpen(true);
    } else {
      setOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath, selectedItem]);

  const handleClick = () => {
    setOpen(!open);
  };

  return (
    <>
      <ListItem
        sx={{
          minHeight: 46,
          width: '100%',
          mb: 1,
          px: 1.5,
          borderRadius: '6px',
          transition: 'background-color 160ms ease',
          '&:hover': { backgroundColor: '#EAF9FE' },
        }}
      >
        {item?.attributes?.icon && <ListItemIcon>{item?.attributes?.icon}</ListItemIcon>}
        <ListItemText
          onClick={() => handleClick(item)}
          primary={item?.attributes?.name}
          sx={{
            flex: 1,
            my: 0,
            span: {
              color: '#231F20',
              fontWeight: isSelectedParent ? 600 : 500,
              fontSize: 14,
            },
            cursor: 'pointer',
          }}
        />
        {!item?.isCategory && (
          <Box
            onClick={(e) => {
              e.preventDefault();
              setOpen(!open);
            }}
          >
            {open ? (
              <Image src="/images/chevron-up.svg" alt="chevron-up" width={24} height={24} />
            ) : (
              <Image src="/images/chevron-down.svg" alt="chevron-up" width={24} height={24} />
            )}
          </Box>
        )}
      </ListItem>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding sx={{ paddingLeft: 3 }}>
          {children.map((child, key) => (
            <MenuItem key={key} item={child} parentSlug={item?.attributes?.slug} />
          ))}
        </List>
      </Collapse>
    </>
  );
};

const MenuItem = ({ item, parentSlug }) => {
  const Component = hasChildren(item?.attributes?.document_pages) ? MultiLevel : SingleLevel;
  return <Component item={item} isCategory parentSlug={parentSlug} />;
};

export default MenuItem;
